import ko from "knockout";
import { Listview } from "../../../listview";
import { CustomError, customFetch as fetch } from "../../../fetch";
import toastr from "toastr";
import h from "virtual-dom/h";
import { errorToast, successToast } from "../../../toastr-helper";
import { closePopups, openPopup } from "../../../init";

export default class ContactInfo {
    parent: any;
    contactModel: KnockoutObservable<ContactModel>;
    validationErrors: KnockoutObservableArray<CustomError>;
    contactError: KnockoutObservable<string>;
    isNew: KnockoutObservable<boolean>;
    idToEdit: KnockoutObservable<string>;
    listview: Listview;

    viewModel: any;
    constructor(parent: any) {
        this.parent = parent;
        this.contactModel = ko.observable<ContactModel>(new ContactModel(null, this));
        this.idToEdit = ko.observable<string>('');
        this.isNew = ko.observable<boolean>(false);
        this.validationErrors = ko.observableArray<CustomError>();
        this.contactError = ko.observable<string>('').extend({
            errorHandler: { source: this.validationErrors, id: 'Contacts' }
        });
        this.listview = new Listview({
            rootElementId: 'contacts',
            pagerOptions: { pagerElementId: 'contacts-pager'},
            getItems: async () => {
                const response = await fetch(`/admin/CompanyOnboarding/ContactsAll/${parent.companyId}`);
                if (response.ok) {
                    return response.data;
                }
                else {
                    toastr.error('Er gaat iets mis met het ophalen van de contact gegevens. Mogelijk is de data niet geldig, neem contact op met de developers.', 'Foutmelding', {
                        timeOut: 0,
                        extendedTimeOut: 0
                    });
                }
            },
            columns: [
                {
                    position: 1,
                    header: 'Naam',
                    render: (item: any) =>
                        h('a', {
                            href: '#',
                            onclick: (e: Event) => {
                                e.preventDefault();
                                this.editContact(item.id);
                            }
                        }, item.fullName)
                },
                {
                    position: 2,
                    header: 'Email',
                    property: 'email',
                    render: (item: any) =>
                        h('a', { href: `mailto:${item.email}` }, item.email)
                },
                {
                    position: 3,
                    header: 'Mobiel',
                    property: 'mobile'
                },
                {
                    position: 4,
                    header: 'Functie',
                    property: 'position'
                },
                {
                    position: 5,
                    header: 'Verantwoordelijkheden',
                    props: { class: 'pointer, comments' },
                    render: (item: any) => this.contactModel().populateEnumValue(item.responsibility)
                },
                {
                    position: 6,
                    props: { className: 'text-right' },
                    actions: [
                        {
                            name: 'Verwijderen',
                            position: 1,
                            render: () => {
                                let icon = h('i', { className: 'fa fa-times' }, []);

                                return h('a', {
                                    href: '#',
                                    title: 'Verwijderen'
                                }, [icon]);
                            },
                            type: 'confirm-popup',
                            confirmText: 'Weet u zeker dat u dit contact wilt verwijderen?',
                            onConfirm: async (item: any) => {
                                if (await this.deleteContact(item.id)) {
                                    successToast('Het contact is verwijderd');

                                    this.listview.load()
                                }
                                else {
                                    errorToast('Er ging iets mis met het verwijderen van het contact');
                                }
                            }
                        }
                    ]
                }
            ]
        })

        this.listview.load();
    }

    getServiceUrl(){
        return this.isNew() ?
            `/admin/CompanyOnboarding/AddContact/${this.parent.companyId}` :
            `/admin/CompanyOnboarding/EditContact/${this.idToEdit()}`;
    }

    editContact(id: string){
        this.isNew(false);
        this.idToEdit(id);
        const contact = this.listview.items.find((c: any) => c.id == id);

        this.contactModel(new ContactModel(contact, this));
        openPopup('contact-popup');
    }

    addContact(){
        this.isNew(true);
        this.contactModel(new ContactModel(null, this));
        openPopup('contact-popup');
    }

    async deleteContact(id: string){
        const response = await fetch(`/admin/CompanyOnboarding/DeleteContact/${id}`, {
            method: 'DELETE'
        });

        return response.ok;
    }

    async validateModel() {
        const url = `/admin/CompanyOnboarding/ValidateContacts/${this.parent.companyId}`;

        const response = await fetch(url, {
            method: 'GET',
        });

        if (!response.ok) {
            this.parent.validationIsSuccessful = false;
            if (response.errors && response.errors.length > 0) {
                this.validationErrors(response.errors);
            }
        }
    }
}

class ContactModel {
    parent: ContactInfo;
    validationErrors: KnockoutObservableArray<CustomError>;

    firstName: KnockoutObservable<string>;
    lastName: KnockoutObservable<string>;
    email: KnockoutObservable<string>;
    mobile: KnockoutObservable<string>;
    position: KnockoutObservable<string>;
    comments: KnockoutObservable<string>;

    selectedResponsibilities: KnockoutObservableArray<string>;

    constructor(model: any, parent: ContactInfo) {
        this.parent = parent;
        this.validationErrors = ko.observableArray<CustomError>();

        this.firstName = ko.observable<string>(model?.firstName || '')
            .extend({ errorHandler: { source: this.validationErrors, id: 'FirstName' } });
        this.lastName = ko.observable<string>(model?.lastName || '')
            .extend({ errorHandler: { source: this.validationErrors, id: 'LastName' } });
        this.email = ko.observable<string>(model?.email || '')
            .extend({ errorHandler: { source: this.validationErrors, id: 'Email' } });
        this.mobile = ko.observable<string>(model?.mobile || '')
            .extend({ errorHandler: { source: this.validationErrors, id: 'Mobile' } });
        this.position = ko.observable<string>(model?.position || '')
        this.comments = ko.observable<string>(model?.comments || '');

        this.selectedResponsibilities = ko.observableArray([]);
        this.populateEnumValue(model?.responsibility || 0)
    }

    private toFormData() {
        var fd = new FormData();

        fd.append('firstName', this.firstName());
        fd.append('lastName', this.lastName());
        fd.append('email', this.email());
        fd.append('mobile', this.mobile());
        fd.append('position', this.position());
        fd.append('comments', this.comments());

        fd.append('responsibility', this.calculateEnumValue().toString());

        return fd;
    }

    calculateEnumValue(){
        let value = 0;

        this.selectedResponsibilities().forEach(function (responsibility) {
            switch (responsibility) {
                case "Hr":
                    value |= 1 << 0;
                    break;
                case "Finance":
                    value |= 1 << 1;
                    break;
                case "Owner":
                    value |= 1 << 2;
                    break;
                case "Contracts":
                    value |= 1 << 3;
                    break;
            }
        });

        return value;
    }
    populateEnumValue(enumValue: number){
        this.selectedResponsibilities([]);

        if (enumValue & (1 << 0)) this.selectedResponsibilities.push("Hr");
        if (enumValue & (1 << 1)) this.selectedResponsibilities.push("Finance");
        if (enumValue & (1 << 2)) this.selectedResponsibilities.push("Eigenaar");
        if (enumValue & (1 << 3)) this.selectedResponsibilities.push("Contracten");

        return this.selectedResponsibilities().join(' | ');
    }
    async submit() {
        var body = this.toFormData();
        
        var response = await fetch(this.parent.getServiceUrl(), {
            method: 'POST',
            body: body
        });

        if (response.ok) {
            successToast('Het contact is opgeslagen');

            closePopups();
            this.parent.listview.load();
        }
        else {
            errorToast('Er ging iets mis met het opslaan');

            if (response.errors && response.errors.length > 0) {
                this.validationErrors(response.errors);
            }
        }
    }
}
